import type { PixelCrop } from "react-image-crop";

function toProxiedUrl(url: string): string {
  if (url.startsWith("data:") || url.startsWith("/")) return url;
  return `/api/image-proxy?url=${encodeURIComponent(url)}`;
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.setAttribute("crossOrigin", "anonymous");
    img.src = toProxiedUrl(url);
  });
}

/**
 * Applies a pixel crop to an image and returns a base64 JPEG data URL.
 * Pixel coordinates are in the image's natural (full-resolution) space.
 * External URLs are routed through /api/image-proxy to bypass CORS.
 */
export async function getCroppedImage(
  imageSrc: string,
  pixelCrop: PixelCrop,
  quality = 0.9
): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return canvas.toDataURL("image/jpeg", quality);
}
